/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { ellipsis, naturalSort } from '@utils/index';
import { formatDate } from '@utils/date';
import React from 'react';
import {
  getActual,
  getBaseline,
  getTarget
} from '@selectors/universal-tracker-values';
import { UniversalTrackerMin, UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { getChangeOfDirection, getChartDataByValues } from '@utils/charts';
import { loadUniversalTrackerModal } from '@actions/universalTrackerModal';
import { UtrTableChart } from './UtrTableChart';
import { Button } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { UtrTableValue } from './UtrTableValue';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Table, ColumnDef } from '@g17eco/molecules/table';
import { SortingFn } from '@tanstack/react-table';
interface UtrTableProps {
  utrv: { loaded: boolean };
  data: UniversalTrackerPlain[];
  rows?: number;
  className?: string;
}

interface RowData {
  utrId: string;
  col1: JSX.Element;
  col2: JSX.Element;
  col3: string;
  col4: JSX.Element;
  col5: JSX.Element;
  col6: JSX.Element;
  col7: JSX.Element;
}

const getClassName = (targetDirection: string, changeDirection: number) => {
  if (changeDirection > 0) {
    const arrowDirection = targetDirection === 'decrease' ? 'fa-caret-down' : 'fa-caret-up';
    return `fas ${arrowDirection} fa-2x text-ThemeSuccessMedium`;
  } else if (changeDirection < 0) {
    const arrowDirection = targetDirection === 'decrease' ? 'fa-caret-up' : 'fa-caret-down';
    return `fas ${arrowDirection} fa-2x text-ThemeDangerMedium`;
  }
  return 'fas fa-circle fa-md text-ThemeAccentMedium';
}

const getChangeDirection = (universalTracker: UniversalTrackerPlain, chartData: any) => {
  if (chartData.length === 0) {
    return 0;
  }

  const targetDirection = universalTracker.targetDirection === 'decrease' ? -1 : 1;
  return getChangeOfDirection(chartData, targetDirection);
};

const sortNumber = (columnKey: keyof RowData): SortingFn<RowData> => (a, b): number => {
  const isReactElement = columnKey !== 'utrId' && columnKey !== 'col3';
  if (isReactElement) {
    const aValue = a?.original[columnKey]?.props?.value ?? -1;
    const bValue = b?.original[columnKey]?.props?.value ?? -1;
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
  }
  return 0;
}

const columns: ColumnDef<RowData>[] = [
  {
    id: 'arrow',
    header: '',
    meta: {
      cellProps: {
        className: 'text-left text-md-center flex-grow-1 flex-md-grow-0 colArrow order-1',
      },
    },
    cell: ({ row }) => row.original.col1,
  },
  {
    id: 'universalTracker',
    accessorKey: 'col2',
    header: 'Metric Name',
    meta: {
      cellProps: {
        className: 'text-left rt-utr-name row-break-1 row-md-break-0 order-7 order-md-2',
      },
    },
    cell: ({ row }) => row.original.col2,
    sortingFn: (a, b) => {
      return naturalSort(a.original.col2.props.text ?? '', b.original.col2.props.text ?? '');
    },
  },
  {
    accessorKey: 'col3',
    header: 'Updated',
    meta: {
      cellProps: {
        className: 'numeric text-center d-none d-md-block order-3',
      },
    },
  },
  {
    accessorKey: 'col4',
    header: 'Baseline',
    meta: {
      cellProps: {
        className: 'numeric text-center d-none d-md-block order-4',
      },
    },
    cell: ({ row }) => row.original.col4,
    sortingFn: sortNumber('col4'),
  },
  {
    accessorKey: 'col5',
    header: 'Value',
    meta: {
      cellProps: {
        className: 'numeric text-center order-5',
      },
    },
    cell: ({ row }) => row.original.col5,
    sortingFn: sortNumber('col5'),
  },
  {
    accessorKey: 'col6',
    header: 'Target',
    meta: {
      cellProps: {
        className: 'numeric text-center order-5',
      },
    },
    cell: ({ row }) => row.original.col6,
    sortingFn: sortNumber('col6'),
  },
  {
    accessorKey: 'col7',
    header: 'Chart',
    meta: {
      cellProps: {
        className: 'text-right colChart d-none d-lg-block order-5',
      },
    },
    cell: ({ row }) => row.original.col7,
    enableSorting: false,
  },
];

export const UtrTableView = (props: UtrTableProps) => {

  const { data, utrv, rows: pageSize = 10, className } = props;
  const chartValues = useAppSelector(state => state.utrvChartData.data);
  const dispatch = useAppDispatch();

  // the getActual dispatch relies on this state
  // we need to have a way to track changes so that useMemo refreshes internal state
  const dataStateLastUpdated = useAppSelector(state => state.universalTrackerValues.lastUpdated);

  const getEffectiveDate = React.useCallback((universalTracker: UniversalTrackerMin) => {
    return dispatch(getActual(universalTracker, 'effectiveDate'));
  }, [dispatch]);

  const isUtrvLoaded = utrv && utrv.loaded;

  const tableData: RowData[] = React.useMemo(() => {
    if (!dataStateLastUpdated) {
      // This is just to prevent the compilation error
      // React.useMemo has an unnecessary dependency
      return [];
    }
    return data.map(universalTracker => {
      const chartData = getChartDataByValues(chartValues[universalTracker._id]);
      const changeDirection = getChangeDirection(universalTracker, chartData);
      const targetDirection = universalTracker.targetDirection;
      const className = getClassName(targetDirection as string, changeDirection);

      const baseline = dispatch(getBaseline(universalTracker));
      const actual = dispatch(getActual(universalTracker));
      const target = dispatch(getTarget(universalTracker));

      return {
        utrId: universalTracker._id,
        col1: <i className={className} />,
        col2: <SimpleTooltip text={universalTracker.name}>
          <Button color='link' onClick={() => {
            dispatch(loadUniversalTrackerModal({ universalTrackerId: universalTracker._id }))
          }}>{ellipsis(universalTracker.name, 100)}</Button>
        </SimpleTooltip>,
        col3: formatDate(getEffectiveDate(universalTracker) as unknown as string, 'MMM YYYY'),
        col4: <UtrTableValue universalTracker={universalTracker} utrv={baseline} />,
        col5: <UtrTableValue universalTracker={universalTracker} utrv={actual} />,
        col6: <UtrTableValue universalTracker={universalTracker} utrv={target} />,
        col7: <UtrTableChart universalTracker={universalTracker} changeDirection={changeDirection} />
      }
    });
  }, [data, chartValues, dispatch, getEffectiveDate, dataStateLastUpdated]);

  return (
    <LoadingPlaceholder isLoading={!isUtrvLoaded} height={200}>
      <div className={'react-table-wrapper'}>
        <Table
          responsive={true}
          data={tableData}
          pageSize={pageSize}
          className={'utr-table ' + className}
          sortBy={[{ id: 'universalTracker', desc: false }]}
          columns={columns}
        />
      </div>
    </LoadingPlaceholder>
  );
}
